import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, CheckCircle2, ChevronDown, ChevronUp, ClipboardCheck, Clock, Eye, MapPin, MessageSquare, ShieldOff, Users, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatDate } from '../utils/formatters';

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Organisers' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'revoked', label: 'Access removed' },
];

function formatTime(value) {
  if (!value) return 'N/A';
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours)) return value;
  return `${hours % 12 || 12}:${String(minutes || 0).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
}

export default function OpenPlayOrganizerApplications() {
  const [status, setStatus] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [expandedOrganizerId, setExpandedOrganizerId] = useState(null);
  const [sessionsByOrganizer, setSessionsByOrganizer] = useState({});
  const [loadingSessionsId, setLoadingSessionsId] = useState(null);

  const loadApplications = useCallback(async () => {
    setLoading(true);
    try {
      setApplications(await adminService.getOpenPlayOrganizerApplications(status));
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load organiser applications');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const approve = async (application) => {
    setActingId(application.id);
    try {
      await adminService.approveOpenPlayOrganizerApplication(application.id);
      toast.success(`${application.name} is now an approved organiser`);
      await loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not approve application');
    } finally {
      setActingId(null);
    }
  };

  const reject = async (application) => {
    const note = reviewNote.trim();
    if (note.length < 3) {
      toast.error('Enter a brief review note for the player');
      return;
    }
    setActingId(application.id);
    try {
      await adminService.rejectOpenPlayOrganizerApplication(application.id, note);
      toast.success('Organiser application rejected');
      setRejectingId(null);
      setReviewNote('');
      await loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not reject application');
    } finally {
      setActingId(null);
    }
  };

  const toggleSessions = async (application) => {
    if (expandedOrganizerId === application.id) {
      setExpandedOrganizerId(null);
      return;
    }

    setExpandedOrganizerId(application.id);
    if (sessionsByOrganizer[application.id]) return;

    setLoadingSessionsId(application.id);
    try {
      const sessions = await adminService.getOpenPlayOrganizerSessions(application.id);
      setSessionsByOrganizer((current) => ({ ...current, [application.id]: sessions }));
    } catch (error) {
      setExpandedOrganizerId(null);
      toast.error(error.response?.data?.message || 'Could not load organiser sessions');
    } finally {
      setLoadingSessionsId(null);
    }
  };

  const revoke = async (application) => {
    const sessionLabel = application.activeOpenPlaySessionCount
      ? ` They currently have ${application.activeOpenPlaySessionCount} active Open Play session${application.activeOpenPlaySessionCount === 1 ? '' : 's'}, which will remain available for admin management.`
      : '';
    if (!window.confirm(`Remove Open Play organiser access for “${application.name}”? They will no longer be able to create or manage Open Play sessions.${sessionLabel}`)) return;

    setActingId(application.id);
    try {
      await adminService.revokeOpenPlayOrganizerAccess(application.id);
      toast.success(`Organiser access removed for ${application.name}`);
      await loadApplications();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Could not remove organiser access');
    } finally {
      setActingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#1F2937]">Organiser applications</h1>
        <p className="mt-1 text-sm text-[#667085]">Review players before they can host and manage Open Play sessions.</p>
      </div>

      <div className="admin-card">
        <div className="mb-5 flex flex-wrap gap-2 border-b border-[#EAECF0] pb-5">
          {TABS.map((tab) => (
            <button key={tab.key} type="button" onClick={() => { setStatus(tab.key); setRejectingId(null); setReviewNote(''); }} className={status === tab.key ? 'admin-tab-active' : 'admin-tab-inactive'}>{tab.label}</button>
          ))}
        </div>

        {loading ? <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-admin-600 border-t-transparent" /></div>
          : applications.length === 0 ? <div className="py-12 text-center text-[#667085]"><ClipboardCheck className="mx-auto mb-3" size={40} /><p className="text-sm">No {status} organiser applications.</p></div>
          : <div className="space-y-3">{applications.map((application) => (
            <article key={application.id} className="rounded-xl border border-[#EAECF0] p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2"><h2 className="font-semibold text-[#1F2937]">{application.name}</h2><span className={application.status === 'Approved' ? 'admin-badge-success' : application.status === 'Rejected' || application.status === 'Revoked' ? 'admin-badge-danger' : 'admin-badge-warning'}>{application.status}</span></div>
                  <p className="mt-1 text-sm text-[#475467]">{application.email}{application.phoneNumber ? ` · ${application.phoneNumber}` : ''}</p>
                  <p className="mt-2 text-xs text-[#667085]">Applied {formatDate(application.openPlayOrganizerAppliedAt)}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {status !== 'pending' && <button type="button" onClick={() => toggleSessions(application)} disabled={loadingSessionsId === application.id} className="admin-btn-outline !h-10 !rounded-xl !px-4 text-xs"><Eye size={15} className="mr-1" />{loadingSessionsId === application.id ? 'Loading…' : 'View sessions'}{expandedOrganizerId === application.id ? <ChevronUp size={15} className="ml-1" /> : <ChevronDown size={15} className="ml-1" />}</button>}
                  {status === 'approved' && <button type="button" onClick={() => revoke(application)} disabled={actingId === application.id} className="admin-btn-outline !h-10 !rounded-xl !border-red-500 !px-4 !text-red-600 text-xs"><ShieldOff size={15} className="mr-1" />{actingId === application.id ? 'Removing…' : 'Remove access'}</button>}
                  {status === 'pending' && rejectingId !== application.id && <><button type="button" onClick={() => approve(application)} disabled={actingId === application.id} className="admin-btn-primary !h-10 !rounded-xl !px-4 text-xs"><CheckCircle2 size={15} className="mr-1" />Approve</button><button type="button" onClick={() => setRejectingId(application.id)} disabled={actingId === application.id} className="admin-btn-outline !h-10 !rounded-xl !border-red-500 !px-4 !text-red-600 text-xs"><XCircle size={15} className="mr-1" />Reject</button></>}
                </div>
              </div>

              {status !== 'pending' && <p className="mt-3 flex items-center gap-1.5 text-xs text-[#667085]"><Users size={14} />{application.openPlaySessionCount || 0} Open Play session{application.openPlaySessionCount === 1 ? '' : 's'}{application.activeOpenPlaySessionCount ? ` · ${application.activeOpenPlaySessionCount} active` : ''}</p>}

              {application.openPlayOrganizerApplicationNote && <div className="mt-4 rounded-lg bg-[#F9FAFB] p-3 text-sm text-[#475467]"><p className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#667085]"><MessageSquare size={14} /> Player note</p>{application.openPlayOrganizerApplicationNote}</div>}

              {rejectingId === application.id && <div className="mt-4 rounded-lg border border-red-100 bg-red-50 p-3"><label className="admin-label">Reason for rejection</label><textarea value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} maxLength={1000} rows="3" className="admin-input h-auto py-3" placeholder="Explain what the player needs to address before applying again." /><div className="mt-3 flex justify-end gap-2"><button type="button" onClick={() => { setRejectingId(null); setReviewNote(''); }} className="admin-btn-outline !h-10 !rounded-xl !px-4 text-xs">Cancel</button><button type="button" onClick={() => reject(application)} disabled={actingId === application.id} className="admin-btn-danger !h-10 !rounded-xl !px-4 text-xs">{actingId === application.id ? 'Rejecting…' : 'Confirm rejection'}</button></div></div>}

              {application.openPlayOrganizerReviewNote && <div className="mt-4 rounded-lg bg-[#F9FAFB] p-3 text-sm text-[#475467]"><p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[#667085]">Review note</p>{application.openPlayOrganizerReviewNote}</div>}

              {expandedOrganizerId === application.id && <div className="mt-4 rounded-xl border border-[#DDE9E8] bg-[#F8FCFB] p-4"><div className="mb-3"><h3 className="font-semibold text-[#1F2937]">Open Play sessions</h3><p className="mt-1 text-xs text-[#667085]">Sessions hosted by this organiser. Select one to review or manage it.</p></div>{loadingSessionsId === application.id ? <div className="py-5 text-center text-sm text-[#667085]">Loading sessions…</div> : sessionsByOrganizer[application.id]?.length ? <div className="space-y-2">{sessionsByOrganizer[application.id].map((session) => <div key={session.id} className="flex flex-col gap-3 rounded-lg border border-[#E2E8F0] bg-white p-3 sm:flex-row sm:items-center sm:justify-between"><div><div className="flex flex-wrap items-center gap-2"><p className="font-medium text-[#1F2937]">{session.name}</p><span className={session.status === 'Active' ? 'admin-badge-success' : session.status === 'Cancelled' ? 'admin-badge-danger' : 'admin-badge-neutral'}>{session.status}</span>{session.isPrivate && <span className="admin-badge-neutral">Private</span>}</div><div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#667085]"><span className="flex items-center gap-1"><MapPin size={13} />{session.venueName}</span><span className="flex items-center gap-1"><Calendar size={13} />{formatDate(session.date)}</span><span className="flex items-center gap-1"><Clock size={13} />{formatTime(session.startTime)}–{formatTime(session.endTime)}</span><span className="flex items-center gap-1"><Users size={13} />{session.playerCount} / {session.maxPlayers}</span></div></div><button type="button" onClick={() => window.open(`/openplays/${session.id}`, '_self')} className="admin-btn-outline !h-9 !rounded-xl !px-3 text-xs">Open session</button></div>)}</div> : <div className="rounded-lg bg-white py-5 text-center text-sm text-[#667085]">No Open Play sessions have been created by this organiser.</div>}</div>}
            </article>
          ))}</div>}
      </div>
    </div>
  );
}
